import { useEffect, useState } from 'react'
import { useHistory, useParams, Link } from 'react-router-dom';
import { CourseGet, CourseUpdate } from "services/courseService"
import { Course } from "models/model"
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { fetchLicenseClasses } from 'store/licenseSlice'
import CustomDatePicker from 'components/DatePickerVi'
import { toast } from 'react-hot-toast'
import { ArrowLeft, Save } from 'lucide-react';

interface ApiResponse {
    result?: any
    message?: string
}

export default function EditCoursePage() {
    const router = useHistory()
    const {courseId}: any = useParams()
    const dispatch: AppDispatch = useDispatch()

    // Redux state
    const licenseClasses = useSelector((state: RootState) => state.licenseClasses.items)
    const licenseLoading = useSelector((state: RootState) => state.licenseClasses.loading)

    // Form state
    const [formData, setFormData] = useState<Course>({
        id: '',
        name: '',
        classCode: '',
        startDate: '',
        endDate: '',
        teamCount: 0,
        totalDocuments: 0,
        totalExams: 0,
        totalSchedules: 0,
        totalStudents: 0,
        totalTeachers: 0,
        totalVehicles: 0,
        openingDate: '',
        closingDate: '',
        registrationDate: '',
        licenseClassId: '',
        licenseClassName: '',
        isGraduated: false
    })
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Fetch license classes and course data
    useEffect(() => {
        if (licenseClasses.length === 0) {
            dispatch(fetchLicenseClasses())
        }

        const fetchCourseData = async () => {
            setIsLoading(true)
            try {
                const response: ApiResponse = await CourseGet(courseId)
                if (response.result) {
                    setFormData({
                        ...response.result,
                        startDate: response.result.startDate ? new Date(response.result.startDate) : null,
                        endDate: response.result.endDate ? new Date(response.result.endDate) : null,
                        openingDate: response.result.openingDate ? new Date(response.result.openingDate) : null,
                        closingDate: response.result.closingDate ? new Date(response.result.closingDate) : null,
                        registrationDate: response.result.registrationDate ? new Date(response.result.registrationDate) : null,
                    })
                } else {
                    throw new Error(response.message || 'Không tìm thấy khóa học')
                }
            } catch (error) {
                console.error('Failed to fetch course data:', error)
                setError('Không thể tải dữ liệu khóa học')
                router.push('/courses')
            } finally {
                setIsLoading(false)
            }
        }

        fetchCourseData()
    }, [dispatch, courseId, router, licenseClasses.length])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target
        const checked = (e.target as HTMLInputElement).checked

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleDateChange = (name: 'startDate' | 'endDate' | 'openingDate' | 'closingDate' | 'registrationDate') => (date: Date | null) => {
        setFormData(prev => ({
            ...prev,
            [name]: date
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name || !formData.licenseClassId || !formData.startDate || !formData.endDate || !formData.classCode || !formData.openingDate) {
            setError('Vui lòng điền đầy đủ thông tin bắt buộc')
            return
        }

        if (new Date(formData.startDate) > new Date(formData.endDate)) {
            setError('Ngày kết thúc phải sau ngày bắt đầu')
            return
        }

        setIsSubmitting(true)
        setError('')

        try {
            const response = await CourseUpdate({...formData, id: courseId})
            if (response.result) {
                toast.success('Cập nhật khóa học thành công')
                router.push('/courses')
            } else {
                throw new Error(response.message || 'Cập nhật khóa học thất bại')
            }
        } catch (err: any) {
            setError(err.message || 'Đã xảy ra lỗi khi cập nhật khóa học')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <Link
                    to="/courses"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                >
                    <ArrowLeft className="mr-2" />
                    Quay lại
                </Link>
                <h1 className="text-3xl font-bold text-gray-800">Chỉnh sửa khóa học</h1>
                <div></div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Tên khóa học *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                                placeholder="VD: Khóa học lái xe B2 - K12"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="classCode" className="block text-sm font-medium text-gray-700 mb-1">
                                Mã lớp *
                            </label>
                            <input
                                type="text"
                                id="classCode"
                                name="classCode"
                                value={formData.classCode}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                                placeholder="VD: B2-K12-2023"
                            />
                        </div>
                        <div className="">
                            <label htmlFor="licenseClassId" className="block text-sm font-medium text-gray-700 mb-1">
                                Hạng đào tạo *
                            </label>
                            <select
                                id="licenseClassId"
                                name="licenseClassId"
                                value={formData.licenseClassId}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                                disabled={licenseLoading}
                            >
                                <option value="">Chọn hạng đào tạo</option>
                                {licenseClasses.map((lc) => (
                                    <option key={lc.id} value={lc.id}>
                                        {lc.name}
                                    </option>
                                ))}
                            </select>
                            {licenseLoading && (
                                <p className="text-sm text-gray-500 mt-1">Đang tải danh sách hạng...</p>
                            )}
                        </div>

                        <div className="">
                            <label htmlFor="registrationDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày đăng ký SH *
                            </label>
                            <CustomDatePicker
                                selected={formData.registrationDate}
                                onChange={handleDateChange('registrationDate')}
                                required
                                id="registrationDate"
                                name="registrationDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="openingDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày khai giảng *
                            </label>
                            <CustomDatePicker
                                selected={formData.openingDate}
                                onChange={handleDateChange('openingDate')}
                                required
                                id="openingDate"
                                name="openingDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="closingDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày bế giảng *
                            </label>
                            <CustomDatePicker
                                selected={formData.closingDate}
                                onChange={handleDateChange('closingDate')}
                                required
                                id="closingDate"
                                name="closingDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày bắt đầu *
                            </label>
                            <CustomDatePicker
                                selected={formData.startDate}
                                onChange={handleDateChange('startDate')}
                                required
                                id="startDate"
                                name="startDate"
                            />
                        </div>

                        <div className="">
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                                Ngày kết thúc *
                            </label>
                            <CustomDatePicker
                                selected={formData.endDate}
                                onChange={handleDateChange('endDate')}
                                minDate={formData.startDate ? new Date(formData.startDate) : undefined}
                                required
                                id="endDate"
                                name="endDate"
                            />
                        </div>

                        <div className=" flex items-center">
                            <input
                                type="checkbox"
                                id="isGraduated"
                                name="isGraduated"
                                checked={formData.isGraduated || false}
                                onChange={handleChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isGraduated" className="ml-2 block text-sm text-gray-700">
                                Đã kết thúc
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end mt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Đang cập nhật...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2" />
                                    Cập nhật khóa học
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}